import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { CustomerPaymentMethod } from 'src/features/paymentMethods/utils/types';
import { useUpdateCustomerPaymentMethod } from 'src/features/paymentMethods/api/useUpdateCustomerPaymentMethod';
import { LIST_CUSTOMER_PAYMENT_METHODS_RESOURCE } from 'src/features/paymentMethods/api/useListCustomerPaymentMethods';
import CustomerPaymentMethodFormModal from 'src/features/paymentMethods/components/forms/CustomerPaymentMethodFormModal';
import { paymentMethodsMessages } from 'src/features/paymentMethods/utils/messages';

interface UpdatePaymentMethodFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPaymentMethod: CustomerPaymentMethod;
    children?: React.ReactNode;
}

function UpdatePaymentMethodFormModal<TData>({
    className,
    semantics,
    children,
    selectedPaymentMethod,
}: UpdatePaymentMethodFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateCustomerPaymentMethod({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_CUSTOMER_PAYMENT_METHODS_RESOURCE] });
            toast({
                title: formatMessage(paymentMethodsMessages.updatePaymentMethodSuccess),
                description: formatMessage(paymentMethodsMessages.updatePaymentMethodSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <CustomerPaymentMethodFormModal
            selectedCustomerPaymentMethod={selectedPaymentMethod}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, customerPaymentMethodId: selectedPaymentMethod.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(paymentMethodsMessages.editPaymentMethodSubtitle, {
                entity: selectedPaymentMethod.name,
            })}
        >
            {children}
        </CustomerPaymentMethodFormModal>
    );
}

export default UpdatePaymentMethodFormModal;
